//src/components/layout/Topbar.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  BellRing,
  Building2,
  Check,
  ChevronRight,
  ClipboardCheck,
  LogOut,
  ShieldAlert,
  Sun,
  Moon,
  Search,
  User,
} from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getTenantId } from "@/lib/apiClient";
import { PERMISSIONS } from "@/lib/permissions";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { listNotifications, markNotificationAsRead } from "@/services/notifications";
import type { NotificationItem } from "@/types/notifications";
import { toast } from "sonner";
import { useClientCompanyContext } from "@/hooks/useClientCompanyContext";
import { listAvailableClientCompanies, type ClientCompanyListItem } from "@/services/clientCompanies";
import { listTenants, type TenantListItem } from "@/services/tenants";

const PLATFORM_TENANT_ID_KEY = "normex:platform-selected-tenant-id";
const PLATFORM_TENANT_NAME_KEY = "normex:platform-selected-tenant-name";
const PLATFORM_CLIENT_ID_KEY = "normex:platform-selected-client-id";
const PLATFORM_CLIENT_NAME_KEY = "normex:platform-selected-client-name";

const routeLabels: Record<string, string> = {
  "": "Dashboard",
  cadastros: "Cadastros",
  pessoas: "Pessoas",
  usuarios: "Usuários",
  grupos: "Grupos de Acesso",
  empresa: "Empresa",
  clientes: "Clientes",
  "portal-consultoria": "Dashboard da Empresa",
  "portal-empresa": "Dashboard da Empresa",
  tenant: "Portal da Empresa",
  creditos: "Créditos",
  consumo: "Consumo",
  modelos: "Modelos",
  unidades: "Unidades",
  departamentos: "Departamentos",
  cargos: "Cargos",
  novo: "Novo",
  editar: "Editar",
  onboarding: "Onboarding",
  gro: "GRO",
  dimensoes: "Dimensões",
  "dimensoes-riscos": "Dimensões x Riscos",
  planos: "Planos e Ações",
  "riscos-planos": "Riscos x Planos",
  "planos-por-regra": "Planos por Regra",
  questionarios: "Questionários",
  new: "Novo",
  settings: "Configurações",
  questionnaires: "Questionários",
  campaigns: "Campanhas",
  resultados: "Resultados",
  results: "Resultados",
  dimensions: "Dimensões",
  "dimension-risk-rules": "Dimensões x Riscos",
  "worker-groups": "Grupos de Avaliação",
  "risk-inventories": "Inventários de Risco",
  inventarios: "Inventários de Risco",
  items: "Itens",
  "action-plans": "Planos de Ação",
  "action-plan-models": "Planos de Ação",
  execution: "Execução",
  "risk-suggestions": "Sugestões de Riscos",
  riscos: "Riscos",
  areas: "Áreas/Setores",
  inspecoes: "Inspeções",
  acoes: "Planos de Ação",
  documentos: "Documentos",
  treinamentos: "Treinamentos",
  relatorios: "Relatórios",
  "historico-ai": "Histórico de AI",
  notificacoes: "Notificações",
  financeiro: "Financeiro",
  assinatura: "Assinatura",
  faturas: "Faturas",
  pagamentos: "Pagamentos",
  nfse: "Nota Fiscal",
  configuracoes: "Configurações",
};

function formatSegmentLabel(segment: string) {
  return routeLabels[segment] || segment;
}

function formatRelativeDate(value: string) {
  const date = new Date(value);
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });

  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  const diffDays = Math.round(diffHours / 24);
  return rtf.format(diffDays, "day");
}

function notificationIcon(item: NotificationItem) {
  if (item.type === "campaign_low_engagement" || item.type === "campaign_closing_soon_pending_response") {
    return ShieldAlert;
  }
  if (item.type === "action_plan_item_assigned") {
    return ClipboardCheck;
  }
  return BellRing;
}

function notificationAccent(item: NotificationItem) {
  if (item.severity === "critical") return "border-l-red-500";
  if (item.severity === "warning") return "border-l-amber-500";
  return "border-l-blue-500";
}

export function Topbar() {
  const { user, logout, refreshMe, runContextSwitch } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const isPlatformUser = user?.access_scope === "platform" || user?.context_type === "platform";
  const canAdminPlatformContexts =
    isPlatformUser && (user?.permissions?.includes(PERMISSIONS.PLATFORM_CONTEXT_ADMIN) ?? false);
  const platformSourceTenantId = getTenantId();
  const [platformTenants, setPlatformTenants] = useState<TenantListItem[]>([]);
  const [platformClients, setPlatformClients] = useState<ClientCompanyListItem[]>([]);
  const [isLoadingPlatformTenants, setIsLoadingPlatformTenants] = useState(false);
  const [isLoadingPlatformClients, setIsLoadingPlatformClients] = useState(false);
  const [selectedPlatformTenantId, setSelectedPlatformTenantId] = useState<string | null>(() =>
    window.sessionStorage.getItem(PLATFORM_TENANT_ID_KEY),
  );
  const [selectedPlatformTenantName, setSelectedPlatformTenantName] = useState<string | null>(() =>
    window.sessionStorage.getItem(PLATFORM_TENANT_NAME_KEY),
  );
  const [selectedPlatformClientId, setSelectedPlatformClientId] = useState<string | null>(() =>
    window.sessionStorage.getItem(PLATFORM_CLIENT_ID_KEY),
  );
  const [selectedPlatformClientName, setSelectedPlatformClientName] = useState<string | null>(() =>
    window.sessionStorage.getItem(PLATFORM_CLIENT_NAME_KEY),
  );
  const {
    activeClientCompanies: clientCompanies,
    clearClientCompany,
    hasLoadedClientCompanies,
    hasSelectedClientCompany,
    isLoadingClientCompanies,
    isTenantAdmin,
    refreshClientCompanies,
    selectClientCompany,
    selectedClientCompany,
    selectedClientCompanyId,
  } = useClientCompanyContext();

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const allSegments = location.pathname.split("/").filter(Boolean);
  const inventoryIdIndex = allSegments.findIndex(
    (segment) => uuidRegex.test(segment) && allSegments[allSegments.indexOf(segment) - 1] === "risk-inventories"
  );
  const inventoryId = inventoryIdIndex >= 0 ? allSegments[inventoryIdIndex] : null;

  const breadcrumbSegments = allSegments.reduce<Array<{ segment: string; label: string; path: string }>>((acc, segment, index) => {
    if (uuidRegex.test(segment)) return acc;

    const previous = allSegments[index - 1];
    const next = allSegments[index + 1];
    let path = `/${allSegments.slice(0, index + 1).join("/")}`;

    if (segment === "items" && inventoryId) {
      path = `/gro/risk-inventories/${inventoryId}`;
    }

    if (segment === "action-plans" && previous === "items" && inventoryId && next == null) {
      path = location.pathname;
    }

    acc.push({
      segment,
      label: formatSegmentLabel(segment),
      path,
    });

    return acc;
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const data = await listNotifications({ page: 1, per_page: 5 });
      setNotifications(data.items);
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    let ignore = false;

    setNotifications([]);
    setUnreadCount(0);

    const safeLoadNotifications = async () => {
      try {
        setLoadingNotifications(true);
        const data = await listNotifications({ page: 1, per_page: 5 });

        if (ignore) return;

        setNotifications(data.items);
        setUnreadCount(data.unread_count);
      } catch (error) {
        if (!ignore) {
          console.error(error);
        }
      } finally {
        if (!ignore) {
          setLoadingNotifications(false);
        }
      }
    };

    const handleRefresh = () => {
      if (!ignore) {
        void safeLoadNotifications();
      }
    };

    void safeLoadNotifications();

    const interval = window.setInterval(() => {
      if (!ignore) {
        void safeLoadNotifications();
      }
    }, 60000);

    window.addEventListener("focus", handleRefresh);

    return () => {
      ignore = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", handleRefresh);
    };
  }, [selectedClientCompanyId, selectedPlatformClientId, selectedPlatformTenantId]);

  useEffect(() => {
    if (!isPlatformUser || !canAdminPlatformContexts) {
      setPlatformTenants([]);
      setPlatformClients([]);
      return;
    }

    let active = true;
    setIsLoadingPlatformTenants(true);
    void listTenants({ limit: 200, offset: 0 })
      .then((items) => {
        if (!active) return;
        const activeTenants = items.filter(
          (tenant) => tenant.is_active && tenant.id !== platformSourceTenantId,
        );
        setPlatformTenants(activeTenants);

        if (selectedPlatformTenantId && !activeTenants.some((tenant) => tenant.id === selectedPlatformTenantId)) {
          setSelectedPlatformTenantId(null);
          setSelectedPlatformTenantName(null);
          setSelectedPlatformClientId(null);
          setSelectedPlatformClientName(null);
          window.sessionStorage.removeItem(PLATFORM_TENANT_ID_KEY);
          window.sessionStorage.removeItem(PLATFORM_TENANT_NAME_KEY);
          window.sessionStorage.removeItem(PLATFORM_CLIENT_ID_KEY);
          window.sessionStorage.removeItem(PLATFORM_CLIENT_NAME_KEY);
        }
      })
      .catch((error) => {
        if (active) console.error(error);
      })
      .finally(() => {
        if (active) setIsLoadingPlatformTenants(false);
      });

    return () => {
      active = false;
    };
  }, [canAdminPlatformContexts, isPlatformUser, platformSourceTenantId, selectedPlatformTenantId]);

  useEffect(() => {
    if (!isPlatformUser || !canAdminPlatformContexts || !selectedPlatformTenantId) {
      setPlatformClients([]);
      return;
    }

    let active = true;
    setIsLoadingPlatformClients(true);
    void listAvailableClientCompanies(selectedPlatformTenantId)
      .then((items) => {
        if (!active) return;
        setPlatformClients(items.filter((client) => client.is_active));
        if (selectedPlatformClientId && !items.some((client) => client.id === selectedPlatformClientId && client.is_active)) {
          setSelectedPlatformClientId(null);
          setSelectedPlatformClientName(null);
          window.sessionStorage.removeItem(PLATFORM_CLIENT_ID_KEY);
          window.sessionStorage.removeItem(PLATFORM_CLIENT_NAME_KEY);
        }
      })
      .catch((error) => {
        if (active) console.error(error);
      })
      .finally(() => {
        if (active) setIsLoadingPlatformClients(false);
      });

    return () => {
      active = false;
    };
  }, [canAdminPlatformContexts, isPlatformUser, selectedPlatformClientId, selectedPlatformTenantId]);

  const canLoadClientSelector =
    !!user &&
    hasLoadedClientCompanies &&
    !isLoadingClientCompanies &&
    clientCompanies.length > 0;

  async function handleSelectClientCompany(client: typeof clientCompanies[number] | null) {
    try {
      await runContextSwitch(async () => {
        if (!client) {
          await clearClientCompany();
          navigate("/portal-empresa");
          return;
        }

        await selectClientCompany(client);
        navigate("/");
      });

      if (!client) {
        toast.info("Você voltou para a administração da Empresa.");
      } else {
        toast.success(`Cliente selecionado: ${client.name}`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível trocar o contexto do Cliente.");
    }
  }

  async function handleSelectPlatformTenant(tenant: TenantListItem | null) {
    if (!canAdminPlatformContexts) {
      toast.error("Você não possui permissão para administrar contextos da Plataforma.");
      return;
    }

    try {
      await runContextSwitch(async () => {
        setSelectedPlatformClientId(null);
        setSelectedPlatformClientName(null);
        window.sessionStorage.removeItem(PLATFORM_CLIENT_ID_KEY);
        window.sessionStorage.removeItem(PLATFORM_CLIENT_NAME_KEY);

        if (!tenant) {
          setSelectedPlatformTenantId(null);
          setSelectedPlatformTenantName(null);
          window.sessionStorage.removeItem(PLATFORM_TENANT_ID_KEY);
          window.sessionStorage.removeItem(PLATFORM_TENANT_NAME_KEY);
          await refreshMe();
          navigate("/cadastros/empresa");
          return;
        }

        setSelectedPlatformTenantId(tenant.id);
        setSelectedPlatformTenantName(tenant.name);
        window.sessionStorage.setItem(PLATFORM_TENANT_ID_KEY, tenant.id);
        window.sessionStorage.setItem(PLATFORM_TENANT_NAME_KEY, tenant.name);
        await refreshMe();
        navigate("/portal-empresa");
      });

      if (!tenant) {
        toast.info("Você voltou para a Administração Normex.");
      } else {
        toast.success(`Empresa selecionada: ${tenant.name}`);
      }
    } catch (error) {
      console.error(error);
      if (tenant) {
        setSelectedPlatformTenantId(null);
        setSelectedPlatformTenantName(null);
        window.sessionStorage.removeItem(PLATFORM_TENANT_ID_KEY);
        window.sessionStorage.removeItem(PLATFORM_TENANT_NAME_KEY);
      }
      toast.error(tenant
        ? "Não foi possível entrar na administração desta Empresa."
        : "Não foi possível voltar para a Administração Normex.");
    }
  }

  async function handleSelectPlatformClient(client: ClientCompanyListItem | null) {
    if (!canAdminPlatformContexts || !selectedPlatformTenantId) {
      toast.error("Selecione uma Empresa válida antes de acessar um Cliente.");
      return;
    }

    try {
      await runContextSwitch(async () => {
        if (!client) {
          setSelectedPlatformClientId(null);
          setSelectedPlatformClientName(null);
          window.sessionStorage.removeItem(PLATFORM_CLIENT_ID_KEY);
          window.sessionStorage.removeItem(PLATFORM_CLIENT_NAME_KEY);
          await refreshMe();
          navigate("/portal-empresa");
          return;
        }

        setSelectedPlatformClientId(client.id);
        setSelectedPlatformClientName(client.name);
        window.sessionStorage.setItem(PLATFORM_CLIENT_ID_KEY, client.id);
        window.sessionStorage.setItem(PLATFORM_CLIENT_NAME_KEY, client.name);
        await refreshMe();
        navigate("/");
      });

      if (!client) {
        toast.info("Você voltou para a administração da Empresa.");
      } else {
        toast.success(`Cliente selecionado: ${client.name}`);
      }
    } catch (error) {
      console.error(error);
      if (client) {
        setSelectedPlatformClientId(null);
        setSelectedPlatformClientName(null);
        window.sessionStorage.removeItem(PLATFORM_CLIENT_ID_KEY);
        window.sessionStorage.removeItem(PLATFORM_CLIENT_NAME_KEY);
      }
      toast.error(client
        ? "Não foi possível entrar na administração deste Cliente."
        : "Não foi possível voltar para a administração da Empresa.");
    }
  }

  const accurateUnreadCount = useMemo(() => unreadCount, [unreadCount]);
  const tenantName = user?.tenant?.name?.trim() || null;

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  const handleNotificationClick = async (notification: NotificationItem) => {
    try {
      if (!notification.is_read) {
        await markNotificationAsRead(notification.id);
        setNotifications((current) =>
          current.map((item) =>
            item.id === notification.id
              ? { ...item, is_read: true, read_at: new Date().toISOString() }
              : item
          )
        );
        setUnreadCount((current) => Math.max(0, current - 1));
      }

      if (!notification.route) {
        return;
      }

      const targetRoute = notification.route;

      if (
        notification.type === "campaign_available_to_answer" ||
        notification.type === "campaign_closing_soon_pending_response"
      ) {
        window.open(targetRoute, "_blank", "noopener,noreferrer");
        return;
      }

      navigate(targetRoute);
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível abrir a notificação.");
    }
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <SidebarTrigger className="shrink-0" />

      <Breadcrumb className="hidden md:flex">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Normex</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>

          {breadcrumbSegments.map(({ segment, label, path }, index) => {
            const isLast = index === breadcrumbSegments.length - 1;

            return (
              <BreadcrumbItem key={path}>
                <BreadcrumbSeparator>
                  <ChevronRight className="h-3.5 w-3.5" />
                </BreadcrumbSeparator>

                {isLast || segment === "gro" ? (
                  <span className="font-medium text-foreground">{label}</span>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={path}>{label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex-1" />

      {isPlatformUser ? (
        <>
          <div className="flex h-10 min-w-0 max-w-[170px] shrink-0 items-center gap-2 rounded-2xl border border-border/70 bg-muted/35 px-2 shadow-sm sm:max-w-[220px] sm:px-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              <Building2 className="h-4 w-4" />
            </span>
            <span className="min-w-0 text-left leading-tight">
              <span className="block text-[9px] font-semibold uppercase tracking-wide text-muted-foreground sm:text-[10px]">
                Plataforma
              </span>
              <span className="block truncate text-xs font-semibold text-foreground sm:text-sm">Normex</span>
            </span>
          </div>

          {canAdminPlatformContexts ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={`flex h-10 min-w-0 max-w-[180px] shrink-0 items-center gap-2 rounded-2xl px-2 shadow-sm sm:max-w-[280px] sm:px-3 ${
                  selectedPlatformTenantId
                    ? "border-primary/25 bg-primary/5 hover:bg-primary/10"
                    : "border-dashed bg-background hover:bg-muted/60"
                }`}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Building2 className="h-4 w-4" />
                </span>
                <span className="min-w-0 text-left leading-tight">
                  <span className="block text-[9px] font-semibold uppercase tracking-wide text-primary sm:text-[10px]">Empresa</span>
                  <span className="block truncate text-xs font-semibold text-foreground sm:text-sm">
                    {selectedPlatformTenantName || "Selecionar empresa"}
                  </span>
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 rounded-2xl p-1">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>Empresa / Tenant</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    Selecione uma Empresa para visualizar seus Clientes.
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {isLoadingPlatformTenants ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">Carregando Empresas...</div>
              ) : platformTenants.length > 0 ? (
                platformTenants.map((tenant) => (
                  <DropdownMenuItem
                    key={tenant.id}
                    className="flex cursor-pointer items-center gap-2 rounded-xl"
                    onClick={() => void handleSelectPlatformTenant(tenant)}
                  >
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{tenant.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{tenant.legal_name ?? tenant.slug}</p>
                    </div>
                    {selectedPlatformTenantId === tenant.id ? <Check className="h-4 w-4 text-primary" /> : null}
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhuma Empresa ativa cadastrada.</div>
              )}

              {selectedPlatformTenantId ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer rounded-xl" onClick={() => void handleSelectPlatformTenant(null)}>
                    Voltar para Administração Normex
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
          ) : null}

          {canAdminPlatformContexts && selectedPlatformTenantId ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className={`flex h-10 min-w-0 max-w-[180px] shrink-0 items-center gap-2 rounded-2xl px-2 shadow-sm sm:max-w-[280px] sm:px-3 ${
                    selectedPlatformClientId
                      ? "border-primary/25 bg-primary/5 hover:bg-primary/10"
                      : "border-dashed bg-background hover:bg-muted/60"
                  }`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 text-left leading-tight">
                    <span className="block text-[9px] font-semibold uppercase tracking-wide text-primary sm:text-[10px]">Cliente</span>
                    <span className="block truncate text-xs font-semibold text-foreground sm:text-sm">
                      {selectedPlatformClientName || "Selecionar cliente"}
                    </span>
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 rounded-2xl p-1">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>Clientes de {selectedPlatformTenantName || "Empresa"}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      Clientes pertencentes à Empresa selecionada.
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {isLoadingPlatformClients ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">Carregando Clientes...</div>
                ) : platformClients.length > 0 ? (
                  platformClients.map((client) => (
                    <DropdownMenuItem
                      key={client.id}
                      className="flex cursor-pointer items-center gap-2 rounded-xl"
                      onClick={() => void handleSelectPlatformClient(client)}
                    >
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{client.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{client.legal_name ?? client.slug}</p>
                      </div>
                      {selectedPlatformClientId === client.id ? <Check className="h-4 w-4 text-primary" /> : null}
                    </DropdownMenuItem>
                  ))
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Nenhum Cliente ativo cadastrado nesta Empresa.
                  </div>
                )}

                {selectedPlatformClientId ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="cursor-pointer rounded-xl" onClick={() => void handleSelectPlatformClient(null)}>
                      Voltar para administração da Empresa
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </>
      ) : (
        <>
          {tenantName ? (
            <div className="flex h-10 min-w-0 max-w-[170px] shrink-0 items-center gap-2 rounded-2xl border border-border/70 bg-muted/35 px-2 shadow-sm sm:max-w-[260px] sm:px-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                <Building2 className="h-4 w-4" />
              </span>
              <span className="min-w-0 text-left leading-tight">
                <span className="block text-[9px] font-semibold uppercase tracking-wide text-muted-foreground sm:text-[10px]">
                  Empresa
                </span>
                <span className="block truncate text-xs font-semibold text-foreground sm:text-sm">
                  {tenantName}
                </span>
              </span>
            </div>
          ) : null}

          {canLoadClientSelector ? (
            <DropdownMenu
              onOpenChange={(open) => {
                if (open) void refreshClientCompanies();
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className={`flex h-10 min-w-0 max-w-[170px] shrink-0 items-center gap-2 rounded-2xl px-2 shadow-sm sm:max-w-[280px] sm:px-3 ${
                    selectedClientCompany
                      ? "border-primary/25 bg-primary/5 hover:bg-primary/10"
                      : "border-dashed bg-background hover:bg-muted/60"
                  }`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 text-left leading-tight">
                    <span className="block text-[9px] font-semibold uppercase tracking-wide text-primary sm:text-[10px]">
                      {selectedClientCompany ? "Cliente em operação" : "Cliente"}
                    </span>
                    <span className="block truncate text-xs font-semibold text-foreground sm:text-sm">
                      {selectedClientCompany ? selectedClientCompany.name : "Selecionar cliente"}
                    </span>
                  </span>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-80 rounded-2xl p-1">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>Cliente operacional</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      Selecione o cliente para entrar no contexto de operação.
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {clientCompanies.length > 0 ? (
                  clientCompanies.map((client) => (
                    <DropdownMenuItem
                      key={client.id}
                      className="flex cursor-pointer items-center gap-2 rounded-xl"
                      onClick={() => void handleSelectClientCompany(client)}
                    >
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{client.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{client.legal_name ?? client.slug}</p>
                      </div>
                      {selectedClientCompanyId === client.id ? <Check className="h-4 w-4 text-primary" /> : null}
                    </DropdownMenuItem>
                  ))
                ) : (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Nenhum cliente ativo cadastrado.
                  </div>
                )}

                {hasSelectedClientCompany && isTenantAdmin ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="cursor-pointer rounded-xl" onClick={() => void handleSelectClientCompany(null)}>
                      Voltar para administração da Empresa
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </>
      )}

      <div className="hidden w-64 lg:block">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar..." className="h-9 bg-muted/50 pl-8" />
        </div>
      </div>

      <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-9 w-9">
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>


      <DropdownMenu
        open={notificationsOpen}
        onOpenChange={(nextOpen) => {
          setNotificationsOpen(nextOpen);
          if (nextOpen) {
            void loadNotifications();
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-full">
            <Bell className="h-4 w-4" />
            {accurateUnreadCount > 0 && (
              <Badge className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] text-destructive-foreground">
                {accurateUnreadCount > 9 ? "9+" : accurateUnreadCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-[380px] rounded-2xl p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Notificações</p>
              <p className="text-xs text-muted-foreground">Acompanhe o que precisa da sua atenção.</p>
            </div>
            {accurateUnreadCount > 0 && <Badge variant="secondary">{accurateUnreadCount} novas</Badge>}
          </div>

          <div className="max-h-[360px] overflow-y-auto p-2">
            {loadingNotifications ? (
              <div className="space-y-2 p-2">
                {[0, 1, 2].map((item) => (
                  <div key={item} className="h-16 animate-pulse rounded-xl bg-muted/60" />
                ))}
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => {
                const Icon = notificationIcon(notification);
                return (
                  <DropdownMenuItem
                    key={notification.id}
                    className={`mb-2 flex cursor-pointer items-start gap-3 rounded-xl border border-transparent border-l-4 p-3 focus:bg-muted ${notificationAccent(notification)} ${!notification.is_read ? "bg-primary/5" : "bg-transparent"}`}
                    onClick={() => void handleNotificationClick(notification)}
                  >
                    <div className="mt-0.5 rounded-full bg-background p-2 shadow-sm">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`line-clamp-1 text-sm ${!notification.is_read ? "font-semibold text-foreground" : "font-medium text-foreground/80"}`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && <span className="ml-auto h-2.5 w-2.5 rounded-full bg-primary" />}
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{notification.message}</p>
                      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>{formatRelativeDate(notification.created_at)}</span>
                        {notification.is_general ? <span>Geral</span> : <span>Para você</span>}
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                <BellRing className="h-10 w-10 text-muted-foreground/60" />
                <p className="text-sm font-medium">Nenhuma notificação por aqui</p>
                <p className="max-w-xs text-xs text-muted-foreground">
                  Quando houver algo importante para acompanhar, ele vai aparecer aqui.
                </p>
              </div>
            )}
          </div>

          <DropdownMenuSeparator />
          <div className="p-2">
            <Button
              variant="outline"
              className="h-10 w-full rounded-xl"
              onClick={() => navigate("/notificacoes")}
            >
              Ver todas
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-9 gap-2 px-2">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium md:inline-block">{user?.name}</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => navigate("/configuracoes")}>
            <User className="mr-2 h-4 w-4" />
            Meu Perfil
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-destructive">
            <LogOut className="mr-2 h-4 w-4" />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
